import React from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink, useTheme } from '@mui/material';
import { lighten } from '@mui/system';
import getIconForType from '../../utils/icons/getIconForType';
import placeholderImage from '../../images/placeholder-grey-image.jpg';
import avatarPlaceholder from '../../images/avatar-cropped.png';
import { useEventTracking } from '../../services/eventTrackingService';
import { formatPhoneNumber, isPhoneContactType } from '../../utils/phoneUtils';

const CardDesign4 = ({
    cardId,
    cardFullName,
    cardJobTitle,
    cardCompany,
    cardTheme,
    cardProfilePictureUrl,
    cardCoverPhotoUrl,
    cardCompanyLogoUrl,
    contextCardLayout,
    cardLinkColor,
    cardLocation,
    cardBio,
    cardPronoun,
    formattedPronoun,
    visibleContactLinks,
    previewContactLink,
    handleSaveContact
}) => {
    const theme = useTheme();
    const cardThemeValues = theme.card;
    const design4Theme = cardThemeValues.cardDesign4;

    const tracking = useEventTracking();

    const cardAccentColor = cardTheme || cardLinkColor || '#5e53a3';
    const secondaryAccentColor = lighten(cardAccentColor, 0.85);
    const handleLinkClick = (link, linkType) => {
        if (tracking && link?.details) {
            tracking.trackLinkTap({
                linkType: linkType,
                linkUrl: link.details.linkUrl,
                linkTitle: link.details.linkTitle || linkType,
                cardId: cardId
            });
        }

        if (link?.details?.formattedLink) {
            window.open(link.details.formattedLink, '_blank');
        } else if (link?.details?.linkUrl) {
            const url = link.details.linkUrl.startsWith('http') ?
                link.details.linkUrl :
                `https://${link.details.linkUrl}`;
            window.open(url, '_blank');
        }
    };

    const allLinks = React.useMemo(() => {
        if (!previewContactLink) return visibleContactLinks || [];
        const linkExists = (visibleContactLinks || []).some(
            link => link.type === previewContactLink.type &&
                link.details?.linkUrl === previewContactLink.details?.linkUrl
        );
        return linkExists ? visibleContactLinks : [...(visibleContactLinks || []), previewContactLink];
    }, [visibleContactLinks, previewContactLink]);

    const contactLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['email', 'phone', 'whatsapp', 'sms', 'call'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const socialLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            !['email', 'phone', 'whatsapp', 'sms', 'call', 'website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);

    const websiteLinks = React.useMemo(() => {
        return (allLinks || []).filter(link =>
            ['website', 'address', 'map'].includes(link.type?.toLowerCase())
        );
    }, [allLinks]);
    const renderContactLink = (link, index) => {
        if (!link || !link.details?.linkUrl) return null;

        return (
            <div key={index} style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                <Box
                    onClick={() => handleLinkClick(link, link.type)}
                    sx={{
                        textDecoration: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <Box
                        sx={{
                            width: design4Theme.contactIcon.size,
                            height: design4Theme.contactIcon.size,
                            borderRadius: '50%',
                            backgroundColor: secondaryAccentColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px'
                        }}
                    >
                        {link.details?.icon ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${link.details.icon})`,
                                    backgroundSize: 'contain',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                }}
                            />
                        ) : React.cloneElement(getIconForType(link.type), {
                            sx: {
                                color: cardAccentColor,
                                fontSize: design4Theme.contactIcon.iconSize
                            }
                        })}
                    </Box>
                </Box>

                <Box onClick={() => handleLinkClick(link, link.type)} sx={{ cursor: 'pointer' }}>
                    <Typography
                        sx={{
                            textDecoration: 'none',
                            color: design4Theme.contactValue.color || '#000000',
                            fontWeight: design4Theme.contactValue.fontWeight,
                            fontSize: design4Theme.contactValue.fontSize,
                            display: 'block',
                            lineHeight: 1.5,
                            fontFamily: design4Theme.contactValue.fontFamily
                        }}
                    >
                        {(() => {
                            if (isPhoneContactType(link.type) && link.details?.linkUrl) {
                                return formatPhoneNumber(link.details.linkUrl, 'SG');
                            }
                            return link.details.linkTitle || link.details.linkUrl;
                        })()}
                    </Typography>

                    {link.details.linkLabel && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: design4Theme.contactLabel.color || 'rgba(0, 0, 0, 0.5)',
                                fontSize: design4Theme.contactLabel.fontSize,
                                display: 'block',
                                marginTop: '2px',
                                fontFamily: design4Theme.contactLabel.fontFamily
                            }}
                        >
                            {link.details.linkLabel}
                        </Typography>
                    )}
                </Box>
            </div>
        );
    };

    const renderSocialLinks = () => {
        if (!socialLinks || socialLinks.length === 0) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: design4Theme.socialGrid.gap,
                    mt: design4Theme.socialGrid.marginTop,
                    mb: design4Theme.socialGrid.marginBottom
                }}
            >
                {socialLinks.map((link, index) => (
                    link && link.details?.linkUrl ? (
                        <Box
                            key={index}
                            onClick={() => handleLinkClick(link, link.type)}
                            sx={{
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <Box
                                sx={{
                                    width: design4Theme.socialIcon.size,
                                    height: design4Theme.socialIcon.size,
                                    borderRadius: '50%',
                                    backgroundColor: secondaryAccentColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                    },
                                }}
                            >
                                {link.details?.icon ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${link.details.icon})`,
                                            backgroundSize: 'contain',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat'
                                        }}
                                    />
                                ) : React.cloneElement(getIconForType(link.type), {
                                    sx: {
                                        color: cardAccentColor,
                                        fontSize: design4Theme.socialIcon.iconSize
                                    }
                                })}
                            </Box>
                        </Box>
                    ) : null
                ))}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                maxWidth: cardThemeValues.container.maxWidth,
                boxSizing: 'border-box',
                padding: cardThemeValues.container.padding,
                margin: 'auto',
                backgroundColor: '#ffffff',
                position: 'relative',
                overflow: 'hidden',
                fontFamily: design4Theme.fontFamily
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    borderRadius: design4Theme.container.borderRadius,
                    overflow: 'hidden',
                    boxShadow: design4Theme.container.boxShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}
            >
                <svg
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '300px',
                        zIndex: 1,
                    }}
                    viewBox="0 0 1000 300"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M0,0 
       L0,100 
       Q500,230 1000,100 
       L1000,0 
       Z"
                        fill={cardAccentColor}
                    />
                </svg>

                {cardCompanyLogoUrl && (
                    <Box
                        component="img"
                        src={cardCompanyLogoUrl}
                        alt="Company Logo"
                        sx={{
                            maxWidth: design4Theme.logo.maxWidth,
                            maxHeight: design4Theme.logo.maxHeight,
                            position: 'absolute',
                            top: design4Theme.logo.top,
                            left: design4Theme.logo.left,
                            padding: '8px',
                            zIndex: 2
                        }}
                    />
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 2,
                        marginTop: design4Theme.profilePicture.marginTop
                    }}
                >
                    {cardProfilePictureUrl ? (
                        <Avatar
                            src={cardProfilePictureUrl}
                            alt={cardFullName}
                            sx={{
                                width: design4Theme.profilePicture.size,
                                height: design4Theme.profilePicture.size,
                                border: design4Theme.profilePicture.border,
                                boxShadow: design4Theme.profilePicture.boxShadow
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: design4Theme.profilePicture.size,
                                height: design4Theme.profilePicture.size,
                                borderRadius: '50%',
                                backgroundColor: secondaryAccentColor,
                                color: cardAccentColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: design4Theme.profilePicture.initialsFontSize,
                                fontWeight: 600,
                                border: design4Theme.profilePicture.border,
                                boxShadow: design4Theme.profilePicture.boxShadow
                            }}
                        >
                            {cardFullName ? cardFullName.charAt(0).toUpperCase() : ''}
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        padding: design4Theme.content.padding,
                        textAlign: 'center',
                        marginTop: design4Theme.content.marginTop
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                        }}
                    >
                        <Typography
                            variant="h1"
                            component="h1"
                            sx={{
                                fontSize: design4Theme.typography.nameSize,
                                fontWeight: design4Theme.typography.nameWeight,
                                color: design4Theme.typography.nameColor,
                                marginBottom: design4Theme.typography.nameMarginBottom,
                                marginRight: '0',
                                lineHeight: 1.2,
                                fontFamily: design4Theme.fontFamily
                            }}
                        >
                            {cardFullName}
                        </Typography>
                        {/* {cardPronoun && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: design4Theme.typography.pronounSize,
                                    fontWeight: design4Theme.typography.pronounWeight,
                                    color: design4Theme.typography.pronounColor,
                                    opacity: 0.8,
                                    fontFamily: design4Theme.fontFamily
                                }}
                            >
                                ({formattedPronoun})
                            </Typography>
                        )} */}
                    </Box>

                    {cardJobTitle && (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: design4Theme.typography.jobTitleSize,
                                fontWeight: design4Theme.typography.jobTitleWeight,
                                color: design4Theme.typography.jobTitleColor,
                                marginTop: '5px',
                                marginBottom: '5px',
                                fontFamily: design4Theme.fontFamily
                            }}
                        >
                            {cardJobTitle}
                        </Typography>
                    )}

                    {cardCompany && (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: design4Theme.typography.companySize,
                                fontWeight: design4Theme.typography.companyWeight,
                                color: design4Theme.typography.companyColor,
                                marginBottom: '5px',
                                fontFamily: design4Theme.fontFamily
                            }}
                        >
                            {cardCompany}
                        </Typography>
                    )}

                    {cardLocation && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: design4Theme.typography.locationSize,
                                fontWeight: design4Theme.typography.locationWeight,
                                color: design4Theme.typography.locationColor,
                                opacity: 0.7,
                                marginBottom: '10px',
                                fontFamily: design4Theme.fontFamily
                            }}
                        >
                            {cardLocation}
                        </Typography>
                    )}

                    {cardBio && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: design4Theme.typography.bioSize,
                                fontWeight: design4Theme.typography.bioWeight,
                                color: design4Theme.typography.bioColor,
                                marginTop: '15px',
                                marginBottom: '15px',
                                textAlign: 'center',
                                whiteSpace: 'pre-wrap',
                                fontFamily: design4Theme.fontFamily,
                                lineHeight: 1.2
                            }}
                        >
                            {cardBio}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        padding: design4Theme.contactsSection.padding,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    {contactLinks.map(renderContactLink)}
                    {websiteLinks.map(renderContactLink)}

                    {socialLinks.length > 0 && (
                        <Box sx={{ marginTop: '24px' }}>
                            {renderSocialLinks()}
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        padding: design4Theme.buttonContainer.padding,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSaveContact}
                        sx={{
                            width: '100%',
                            padding: '10px 0',
                            backgroundColor: cardAccentColor,
                            color: '#ffffff',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: design4Theme.saveButton.fontSize,
                            fontWeight: design4Theme.saveButton.fontWeight,
                            boxShadow: `0 4px 8px ${lighten(cardAccentColor, 0.7)}`,
                            '&:hover': {
                                backgroundColor: cardAccentColor,
                            },
                            fontFamily: design4Theme.fontFamily
                        }}
                    >
                        Save Contact
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CardDesign4;

